import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearReaderCalibration } from '../../app/calibration/calibrationSlice';
import {
  selectActiveFriendlyName,
  selectActiveMac,
} from '../../app/calibration/startCalibrationSlice';
import { rfidReaderMap } from '../../utils/tagCalibrationHelpers';

import { Grid, Dialog, Spinner } from '@enterprise-ui/canvas-ui-react';
import { Button } from 'ui-library';
import { RfidGraphic } from '../presentation/RfidGraphic';

import styles from '../../views/Calibration/TagWalkPage.module.scss';
import '../styles/spinnerStyles.scss';

export const HangingCalibrationContainer = ({
  activeState,
  directionState,
  completedChecks,
  lastReaderDetected,
  updateState,
  updateTime,
  stopTimer,
  resetCheck,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeFriendlyName = useSelector(selectActiveFriendlyName);
  const activeMac = useSelector(selectActiveMac);
  const friendlyLocation = activeFriendlyName?.split('-')[1];

  const [isQuitDialogVisible, setIsQuitDialogVisible] = useState(false);
  const [isLagDialogVisible, setIsLagDialogVisible] = useState(false);

  let referencePoint = rfidReaderMap.get(directionState).referencePoint;
  let nextDirection = rfidReaderMap.get(directionState).nextDirection;

  const commonButtons = (
    <>
      <Grid.Item justify="center" align="center">
        <Button
          label="Yes, I'm sure"
          onClick={() => {
            updateState('DONE', directionState);
          }}
        />
      </Grid.Item>
      <Grid.Item justify="center" align="center">
        <Button
          label="Oops, let me try again"
          onClick={() => {
            updateState('', directionState);
            // dispatch(clearReaderCalibration(activeMac));
            resetCheck(true);
          }}
        />
      </Grid.Item>
    </>
  );

  switch (activeState) {
    case 'STARTED':
      return (
        <>
          <p className="hc-ta-center hc-fs-md" data-testid="started">
            Press <strong>Ready</strong> when you're in the correct spot.
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            <Grid.Item justify="center" align="center">
              <Button
                label="Back"
                onClick={() => updateState('', directionState)}
                type="secondary"
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center">
              <Button
                label="Ready"
                onClick={() => {
                  updateTime({ newStart: Date.now() });
                  updateState('IN_PROGRESS', directionState); // event handler should be dispatched based on this
                }}
              />
            </Grid.Item>
          </Grid.Container>
        </>
      );
    case 'IN_PROGRESS':
      return (
        <Grid.Container xs={12} justify="center" align="center">
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-md">
              Please stand still. Getting data...
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-sm spinnerWrapper">
              <span className="calibrationPosition">
                Calibrating <br />
                {friendlyLocation}
              </span>

              <Spinner className="spinnerSize" />
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-sm">
              This may take up to a minute.
            </p>
          </Grid.Item>
          <Grid.Item justify="center" align="center">
            <Button
              label="Back"
              onClick={() => {
                updateState('STARTED', directionState);
                stopTimer();
              }}
              type="secondary"
            />
          </Grid.Item>
          <Dialog
            headingText="Uh oh, something's wrong..."
            bodyText="We're experiencing some technical issues. Can you please try covering the tag with your hand and/or walk a few feet away from the reader?"
            approveButtonText="Got it"
            isVisible={isLagDialogVisible}
            onApprove={() => {
              setIsLagDialogVisible(false);
            }}
          />
        </Grid.Container>
      );

    case 'UNKNOWN_LOCATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The test RFID card wasn’t clearly detected in the store.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing near reader{' '}
            <strong>{activeFriendlyName}</strong>?
          </p>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );

    case 'WRONG_LOCATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            Reader <em>{lastReaderDetected}</em> detected you instead of{' '}
            <em>{activeFriendlyName}</em>.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing near reader{' '}
            <strong>{activeFriendlyName}</strong>?
          </p>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'BACKWARDS_READER':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The RFID reader detected you on the wrong side.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing as pictured?
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );

    case 'UNKNOWN_ORIENTATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The RFID reader couldn't tell which side you were standing on.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing as pictured?
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );

    case 'DONE':
      return (
        <Grid.Container
          xs={12}
          justify="center"
          align="center"
          className="hc-mt-lg"
        >
          <Grid.Item xs={12} className={styles.checksWrapper}>
            <p className="hc-ta-center hc-fs-md">
              {completedChecks >= 2
                ? 'We received the calibration data for the reader!'
                : "Okay, let's do the next check!"}
            </p>
            <p className={styles.checksProgress}>
              &#x2705;{' '}
              <span>
                <strong>{completedChecks} out of 2</strong>
              </span>{' '}
              checks completed
            </p>
          </Grid.Item>

          <Grid.Item xs={12} justify="center" align="center">
            <Button
              label="Next"
              onClick={() => {
                if (completedChecks < 2) {
                  updateState('', nextDirection);
                } else if (completedChecks === 2) {
                  dispatch(clearReaderCalibration(activeMac));
                  navigate('/completedCalibration');
                }
              }}
            />
          </Grid.Item>
        </Grid.Container>
      );

    default:
      return (
        <>
          <Grid.Item
            data-testid="mainContent"
            xs={12}
            className="center-flex-block"
          >
            <p className="hc-ta-center hc-fs-md">To begin calibrating:</p>
            <ol>
              <li>
                Position yourself within <strong>3-5 ft</strong> of the reader
                (see diagram)
              </li>
              <li>
                Stand facing the <strong>{referencePoint}</strong>
              </li>
              <li>
                Hold the <strong>test card</strong> high above your head
              </li>
            </ol>
          </Grid.Item>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            <Grid.Item justify="center" align="center">
              <Button
                label="Back"
                onClick={() => {
                  if (completedChecks > 0) {
                    setIsQuitDialogVisible(true);
                  } else {
                    navigate('/calibrationSetupReader');
                  }
                }}
                type="secondary"
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center">
              <Button
                label="Next"
                onClick={() => {
                  updateState('STARTED', directionState);
                }}
              />
            </Grid.Item>
            <Dialog
              headingText="Are you sure?"
              bodyText="A calibration session is in progress. If you go back, the current session data will be lost."
              approveButtonText="Yes"
              isVisible={isQuitDialogVisible}
              onApprove={() => {
                setIsQuitDialogVisible(false);
                navigate('/calibrationSetupReader');
                dispatch(clearReaderCalibration(activeMac));
              }}
              onRefuse={() => {
                setIsQuitDialogVisible(false);
              }}
              refuseButtonText="Cancel"
            />
          </Grid.Container>
        </>
      );
  }
};
